/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form-main');
      this.optionSelectors = this.querySelectorAll('.option-selector');
      this.data = this.getProductData();
      this.variant = this.getSelectedVariant();
      this.selectedOptions = this.getSelectedOptions();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;
      this.basePrices = null;
      this.b2bPrices = this.getB2BPrices();

      this.addEventListener('change', this.handleVariantChange.bind(this));

      // Initialize base price fetcher if available (skip when B2B inline prices are present)
      if (!this.b2bPrices) {
        this.initBasePriceFetcher();
      }

      // Defer these to allow for quick-add modal to full initialize
      setTimeout(() => {
        this.updateAvailability();
        this.updateAddToCartButton();
      });
    }

    isStaffDiscountCustomer() {
      const b2bCheckEl = document.querySelector('.theme__b2b-check');
      return b2bCheckEl && b2bCheckEl.dataset.isStaffDiscount === 'true';
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      if (this.variant) {
        this.updateMedia();
        this.updateUrl(evt);
        this.updateVariantInput();
      }

      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateMetafieldVisibility();
      VariantPicker.updateLabelText(evt);

      if (!this.preSelection) {
        this.dispatchEvent(new CustomEvent('on:variant:change', {
          bubbles: true,
          detail: {
            form: this.productForm,
            variant: this.variant,
            product: this.data.product
          }
        }));
      }
    }

    /**
     * Initializes base price fetcher
     */
    initBasePriceFetcher() {
      // Wait for BasePriceFetcher to be available
      if (!window.BasePriceFetcher) {
        setTimeout(() => this.initBasePriceFetcher(), 100);
        return;
      }

      if (this.data.product.id) {
        const productGid = `gid://shopify/Product/${this.data.product.id}`;
        window.BasePriceFetcher.fetchBasePrices(productGid).then((basePrices) => {
          this.basePrices = basePrices;
          console.log('VariantPicker: Loaded base prices', basePrices); // eslint-disable-line
          // Update price display with base prices
          this.updatePrice();
        }).catch((error) => {
          console.error('VariantPicker: Error loading base prices', error); // eslint-disable-line
        });
      }
    }

    /**
     * Shows/hides variant metafield elements on the page as necessary
     */
    updateMetafieldVisibility() {
      document.querySelectorAll('[data-variant-metafield]').forEach((elem) => {
        elem.classList.toggle('hidden', elem.dataset.variantMetafield !== this.variant?.id.toString());
      });
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      this.productForm = this.section.querySelector('.js-product-form-main');
      if (!this.productForm) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.variant.available;

      // Get MOQ from product metafields
      const isStaffDiscount = this.isStaffDiscountCustomer();
      const moqUtils = window.__isalbiMoqUtils;
      const productMoq = parseInt(
        this.data?.product?.metafields?.custom?.minimum_order_quantity?.value || 1,
        10
      );
      const backendOverrideMoq = moqUtils && typeof moqUtils.getGlobalOverrideMoq === 'function'
        ? moqUtils.getGlobalOverrideMoq()
        : null;
      const fallbackOverride = isStaffDiscount ? 1 : null;
      const overrideMoq = Number.isFinite(backendOverrideMoq) && backendOverrideMoq > 0
        ? backendOverrideMoq
        : fallbackOverride;
      const moq = moqUtils && typeof moqUtils.resolveEffectiveMoq === 'function'
        ? moqUtils.resolveEffectiveMoq({ overrideMoq, productMoq, fallback: 1 })
        : (Number.isFinite(overrideMoq) && overrideMoq > 0 ? overrideMoq : (productMoq || 1));
      
      // Get inventory data from variant
      // Use product-inventory element's JSON data (most reliable source)
      let inventoryQty = null; // Use null to track if we've found data
      let inventoryPolicy = 'deny';
      
      if (this.variant) {
        // First, try product-inventory element's JSON data (has all variants)
        const productInventory = this.section.querySelector('product-inventory');
        if (productInventory) {
          const inventoryDataEl = productInventory.querySelector('script[type="application/json"]');
          if (inventoryDataEl) {
            try {
              const inventoryData = JSON.parse(inventoryDataEl.textContent);
              // Match by ID (handle both string and number comparison)
              const variantInventory = inventoryData.find(v => {
                const variantId = this.variant.id;
                const dataId = v.id;
                return (
                  dataId === variantId || 
                  dataId === parseInt(variantId, 10) || 
                  parseInt(dataId, 10) === variantId ||
                  parseInt(dataId, 10) === parseInt(variantId, 10)
                );
              });
              if (variantInventory) {
                inventoryQty = variantInventory.inventory_quantity || 0;
                inventoryPolicy = variantInventory.inventory_policy || 'deny';
              }
            } catch (e) {
              console.error('[MOQ Check] Error parsing inventory data:', e);
            }
          }
          
          // Fallback: use data attributes if JSON not available or didn't find variant
          // Only use if we haven't found data yet (inventoryQty is still null)
          if (inventoryQty === null && productInventory.dataset.inventoryQuantity !== undefined) {
            inventoryQty = parseInt(productInventory.dataset.inventoryQuantity, 10) || 0;
            inventoryPolicy = productInventory.dataset.inventoryPolicy || 'deny';
          }
        }
        
        // Last resort: try variant object or product.variants array
        if (inventoryQty === null) {
          if (this.variant.inventory_quantity !== undefined) {
            inventoryQty = this.variant.inventory_quantity || 0;
            inventoryPolicy = this.variant.inventory_policy || 'deny';
          } else if (this.data?.product?.variants) {
            const fullVariant = this.data.product.variants.find(v => 
              v.id === this.variant.id || 
              parseInt(v.id, 10) === parseInt(this.variant.id, 10)
            );
            if (fullVariant && fullVariant.inventory_quantity !== undefined) {
              inventoryQty = fullVariant.inventory_quantity || 0;
              inventoryPolicy = fullVariant.inventory_policy || 'deny';
            }
          }
        }
      }
      
      // Default to 0 if we still couldn't find data
      if (inventoryQty === null) {
        inventoryQty = 0;
      }
      
      const hasEta = this.data?.product?.metafields?.custom?.eta != null;
      
      // Check if stock is insufficient for MOQ
      // Only apply check when:
      // - Inventory policy is 'deny' (not 'continue')
      // - There is some stock (inventoryQty > 0)
      // - Stock is less than MOQ
      // - Product doesn't have ETA (preorder products are allowed)
      const stockInsufficient = 
        inventoryPolicy === 'deny' && 
        inventoryQty > 0 && 
        inventoryQty < moq && 
        !hasEta;

      // Debug logging (remove in production if needed)
      if (stockInsufficient) {
        console.log('[MOQ Check] Stock insufficient:', {
          inventoryQty,
          moq,
          inventoryPolicy,
          hasEta,
          variantId: this.variant?.id
        });
      }

      this.addBtn.disabled = !variantAvailable || this.preSelection || stockInsufficient;

      if (this.preSelection) {
        this.addBtn.textContent = theme.strings.noSelectedVariant;
      } else if (stockInsufficient && variantAvailable) {
        // Show insufficient stock message when stock < MOQ
        this.addBtn.textContent = (theme.strings && theme.strings.insufficientStockMoq) || 'Insufficient stock to meet minimum order quantity';
      } else {
        const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;
        this.addBtn.textContent = variantAvailable
          ? this.addBtn.dataset.addToCartText
          : unavailableStr;
      }
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;
      const { availabilityMethod } = this.dataset; // 'downward' or 'selection'
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.selectedOptions };
      }

      if (availabilityMethod === 'selection') {
        // Flag all options as unavailable
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        // Flag selector options as available or sold out, depending on the variant availability
        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.product.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                VariantPicker.updateOptionAvailability(optionEl, true, variant.available);
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.product.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === this.selectedOptions[i] || this.selectedOptions[i] === null) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
          });
        });
      }
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default')
            ? ''
            : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the color option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
    }

    /**
     * Updates the product media.
     */
    updateMedia() {
      if (!this.variant.featured_media) return;

      if (this.section.matches('quick-add-drawer')) {
        this.section.updateMedia(this.variant.featured_media.id);
      } else {
        this.mediaGallery = this.mediaGallery || this.section.querySelector('media-gallery');
        if (!this.mediaGallery) return;

        const variantMedia = this.mediaGallery.querySelector(
          `[data-media-id="${this.variant.featured_media.id}"]`
        );
        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-info__price > .price');
      if (!this.price) return;

      let { variant } = this;
      if (this.preSelection) {
        variant = this.data.product.variants[0];
        for (let i = 1; i < this.data.product.variants.length; i += 1) {
          if (this.data.product.variants[i].price < variant.price) variant = this.data.product.variants[i];
        }
      }

      if (this.variant) {
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');

        // B2B path: use inline prices rendered by Liquid (catalog pricing)
        const b2bPrice = this.b2bPrices && this.b2bPrices[variant.id];
        const currentPrice = b2bPrice ? b2bPrice.price : variant.price;

        const formatted = this.data.formatted[variant.id];
        let currentPriceHtml = formatted && formatted.price ? formatted.price : '';
        if ((!currentPriceHtml || currentPriceHtml === '') && currentPrice > 0 && window.BasePriceFetcher) {
          currentPriceHtml = window.BasePriceFetcher.formatPrice(currentPrice);
        } else if ((!currentPriceHtml || currentPriceHtml === '') && currentPrice > 0 && window.theme && window.theme.moneyFormat) {
          const priceInDollars = (currentPrice / 100).toFixed(2);
          currentPriceHtml = window.theme.moneyFormat.replace('{{amount}}', priceInDollars);
        }

        // Always show catalog price; no strike-through logic
        priceCurrentEl.querySelector('.js-label').textContent = theme.strings.regularPrice;
        priceCurrentEl.querySelector('.js-value').innerHTML = currentPriceHtml || '';
        if (priceWasEl) {
          priceWasEl.querySelector('.js-label').textContent = '';
          priceWasEl.querySelector('.js-value').innerHTML = '';
        }

        // Update unit price, if specified.
        if (this.variant.unit_price_measurement) {
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');
          const value = this.variant.unit_price_measurement.reference_value;
          const unit = this.variant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = this.data.formatted[this.variant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
        }

        unitPriceEl.hidden = !this.variant.unit_price_measurement;
        this.price.classList.remove('price--on-sale');
        this.price.classList.toggle('price--sold-out', !this.variant.available && !this.preSelection);
      }

      this.price.querySelector('.price__default').hidden = !this.variant && !this.preSelection;
      this.price.querySelector('.price__no-variant').hidden = this.variant || this.preSelection;
      const from = this.price.querySelector('.price__from');
      if (from) {
        from.hidden = !this.preSelection;
      }
    }

    /**
     * Reads inline B2B variant prices JSON (generated via Liquid).
     * @returns {Object|null}
     */
    getB2BPrices() {
      const script = document.getElementById('b2b-variant-prices');
      if (!script) return null;
      try {
        const parsed = JSON.parse(script.textContent || '{}');
        if (!parsed.variants) return null;
        const map = {};
        parsed.variants.forEach((v) => { map[v.id] = v; });
        window.B2BVariantPrices = map;
        return map;
      } catch (e) {
        console.error('VariantPicker: failed to parse B2B prices', e); // eslint-disable-line
        return null;
      }
    }

    /**
     * Updates the weight.
     */
    updateWeight() {
      this.weights = this.weights || this.section.querySelectorAll('.product-info__weight');
      if (this.weights.length === 0) return;

      const weightAvailable = this.variant && this.variant.weight > 0;
      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable ? this.data.formatted[this.variant.id].weight : '';
        weight.hidden = !weightAvailable;
      });
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcodes = this.barcodes || this.section.querySelectorAll('.product-info__barcode-value');
      if (this.barcodes.length === 0) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
        barcode.parentNode.hidden = !barcodeAvailable;
      });
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.variant.id}`);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms =
        this.forms || this.section.querySelectorAll('.js-product-form-main, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        input.value = this.variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selectedText = selector.querySelector('.custom-select__btn').textContent.trim();
          selectedOptions.push(selectedText === this.dataset.placeholderText ? null : selectedText);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();
      return this.data.product.variants.find(
        (v) => v.options.every((val, index) => val === selectedOptions[index])
      );
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
